"use client"

import * as React from "react"
import { useRouter } from "next/navigation"
import {
  Image as ImageIcon,
  Film,
  Camera,
  Eraser,
  Sparkles,
  Bot,
  CreditCard,
  History as HistoryIcon,
  Wand2,
  LayoutGrid,
  Sun,
  Moon,
  LogOut,
  User,
} from "lucide-react"
import { useTheme } from "next-themes"

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command"
import { useAuth } from "@/components/auth-provider"
import { supabase } from "@/lib/supabase"
import { useAccountModal } from "@/store/account-modal-store"

interface NavAction {
  label: string
  icon: React.ComponentType<{ className?: string }>
  href?: string
  onSelect?: () => void
  shortcut?: string
}

export function CommandPalette() {
  const [open, setOpen] = React.useState(false)
  const router = useRouter()
  const { user } = useAuth()
  const { theme, setTheme } = useTheme()
  const openAccountModal = useAccountModal((s) => s.openModal)

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((v) => !v)
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [])

  const run = React.useCallback(
    (fn: () => void) => {
      setOpen(false)
      requestAnimationFrame(fn)
    },
    [setOpen]
  )

  const goto = React.useCallback(
    (href: string) => run(() => router.push(href)),
    [run, router]
  )

  const create: NavAction[] = [
    { label: "Изображение", icon: ImageIcon, href: "/image-generation" },
    { label: "Видео", icon: Film, href: "/video-generation" },
    { label: "Photoshoot", icon: Camera, href: "/photoshoot" },
    { label: "Удалить фон", icon: Eraser, href: "/background-removal" },
    { label: "Виртуальная примерочная", icon: Wand2, href: "/virtual-try-on" },
    { label: "Аватары", icon: Sparkles, href: "/professional-headshot" },
  ]

  const navigate: NavAction[] = [
    { label: "Главная", icon: LayoutGrid, href: "/" },
    { label: "История генераций", icon: HistoryIcon, href: "/history" },
    { label: "Шаблоны", icon: LayoutGrid, href: "/templates" },
    { label: "AI Ассистент", icon: Bot, href: "/assistant" },
    { label: "Аккаунт и баланс", icon: CreditCard, onSelect: () => run(() => openAccountModal()) },
  ]

  const account: NavAction[] = user
    ? [
        {
          label: theme === "dark" ? "Светлая тема" : "Тёмная тема",
          icon: theme === "dark" ? Sun : Moon,
          onSelect: () => run(() => setTheme(theme === "dark" ? "light" : "dark")),
        },
        {
          label: "Выйти",
          icon: LogOut,
          onSelect: () =>
            run(async () => {
              await supabase.auth.signOut()
              router.push("/")
            }),
        },
      ]
    : [
        {
          label: "Войти",
          icon: User,
          onSelect: () => run(() => router.push("/?auth=1")),
        },
      ]

  return (
    <CommandDialog open={open} onOpenChange={setOpen} title="Командная палитра">
      <CommandInput placeholder="Поиск действий, страниц, моделей..." />
      <CommandList>
        <CommandEmpty>Ничего не найдено.</CommandEmpty>

        <CommandGroup heading="Создать">
          {create.map((a) => (
            <CommandItem
              key={a.label}
              value={a.label}
              onSelect={() => a.href && goto(a.href)}
            >
              <a.icon className="opacity-70" />
              <span>{a.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Навигация">
          {navigate.map((a) => (
            <CommandItem
              key={a.label}
              value={a.label}
              onSelect={() => a.onSelect ? a.onSelect() : (a.href && goto(a.href))}
            >
              <a.icon className="opacity-70" />
              <span>{a.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Аккаунт">
          {account.map((a) => (
            <CommandItem
              key={a.label}
              value={a.label}
              onSelect={a.onSelect}
            >
              <a.icon className="opacity-70" />
              <span>{a.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <div className="px-3 py-2 text-[11px] text-muted-foreground flex items-center gap-2">
          <span>Открыть в любой момент:</span>
          <kbd className="rounded border border-border/60 bg-muted/40 px-1.5 py-0.5 text-[10px] font-medium tabular-nums">⌘ K</kbd>
        </div>
      </CommandList>
    </CommandDialog>
  )
}
